import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js'
import './StationAssets.css'

import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Slider from '@mui/material/Slider/Slider'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IconProvider from '../../components/icons/IconProvider'
import InformativeComponent from '../../components/utilities/InformativeComponent'
import { ProgressContext } from '../../context/ProgressProvider'
import { ISimulatorContext, SimulatorContext } from '../../context/SimulatorProvider'
import { StepperContext } from '../../context/StepperProvider'
import { IVehicleContext, VehicleContext } from '../../context/VehicleProvider'
import { SimulationType, SortType } from '../../data/Simulation'
import { Constants } from '../../data/constants'
import { Product } from '../../data/product'
import { Tariff } from '../../data/tariff'
import { useProductsQuery } from '../../hooks/useProductsQuery'
import { useChargingSimulationQuery } from '../../hooks/useVehiclesQuery'

function StationAssets() {
  const appInsights = useAppInsightsContext()
  const { t } = useTranslation()
  const firstRender = useRef(true)
  const { currentStep, setPreviousDisabled, setNextDisabled } = useContext(StepperContext)
  const { isLoading, setIsLoading } = useContext(ProgressContext)
  const {
    codeTotal,
    simulationType,
    simulationTime,
    initialBatteryLevel,
    targetBatteryLevel,
    loggedInsight,
    setLoggedInsight,
    setTargetBatteryLevel,
    setSelectedProduct,
    setTariff,
    setSimulationTime
  } = useContext(SimulatorContext) as ISimulatorContext
  const [estimatedTime, setEstimatedTime] = useState(simulationTime ?? '30')
  const [selectedId, setSelectedId] = useState('')
  const { basicConnectors, optionalConnectors, vehicle, powerMaxStandard } = useContext(
    VehicleContext
  ) as IVehicleContext
  const [productsSorted, setProductsSorted] = useState<Product[]>()
  const [filteredProducts, setFilteredProducts] = useState<Product[]>()
  const [batteryLevel, setBatteryLevel] = useState<number | string>(targetBatteryLevel ?? 80)
  const [updatedTariff, setUpdatedTariff] = useState<Tariff[]>()
  const [productsNumber, setProductsNumber] = useState(0)
  const [sortType, setSortType] = useState<SortType>(
    simulationType === SimulationType.Battery ? SortType.Time : SortType.Price
  )
  const [charginSessionParams, setChargingSessionParams] = useState<string | undefined>()
  const chargingSimulationDetails = useChargingSimulationQuery(vehicle?.Id, charginSessionParams)
  const productsDetails = useProductsQuery(
    codeTotal.split(Constants.CODE_PREFIX)[1],
    basicConnectors,
    optionalConnectors
  )
  const sliderMarks = [
    {
      value: initialBatteryLevel || 0,
      label: initialBatteryLevel + ' %'
    },
    {
      value: 80,
      label: '80 %'
    }
  ]

  useEffect(() => {
    if (firstRender.current) {
      setPreviousDisabled(false)
      setNextDisabled(true)

      if (!loggedInsight) {
        if (typeof codeTotal !== 'undefined') {
          appInsights.trackEvent({
            name: '[Ma Charge Rapide] Place',
            properties: {
              placeCode: codeTotal
            }
          })
        }
        if (typeof simulationType !== 'undefined' && SimulationType[simulationType]) {
          appInsights.trackEvent({
            name: '[Ma Charge Rapide] Simulation',
            properties: {
              simulationType: SimulationType[simulationType]
            }
          })
        }
        if (typeof vehicle !== 'undefined') {
          appInsights.trackEvent({
            name: '[Ma Charge Rapide] Vehicle profile',
            properties: {
              vehicleBrand: vehicle.Brand,
              vehicleModel: vehicle.Model,
              vehicleVersion: vehicle.Version
            }
          })
        }
        setLoggedInsight(true)
      }
      firstRender.current = false
    }
  })

  useEffect(() => {
    if (productsDetails.isSuccess) {
      if (productsDetails.error) {
        setNextDisabled(true)
        return
      }

      if (productsDetails.data) {
        getUpdatedTariff()
      }
    }
  }, [productsDetails.isSuccess, charginSessionParams])

  useEffect(() => {
    if (chargingSimulationDetails.isSuccess) {
      if (productsDetails.error) {
        setNextDisabled(true)
        return
      }

      if (chargingSimulationDetails.data.data) {
        let newTariff: Tariff[] = []

        // console.log('chargingSimulationDetails', chargingSimulationDetails)
        for (let n of chargingSimulationDetails.data.data) {
          //   console.log('n', n)
          newTariff.push({
            tariffModel: n.priceChargingTransaction.tariffModel,
            estimatedPrice: n.priceChargingTransaction.estimatedPrice,
            durationInMinutes: n.durationInMinutes,
            batteryTargetLevel: n.batteryTargetLevel,
            serviceId: n.serviceId,
            energyQuantity: n.energyQuantity
          })
        }

        setUpdatedTariff(newTariff)
        setTariff(newTariff)
        setIsLoading(false)
      }
    }
  }, [chargingSimulationDetails.isSuccess, charginSessionParams])

  useEffect(() => {
    if (filteredProducts) {
      if (filteredProducts.length > 1) {
        const availableProducts = sortProducts([...filteredProducts])
        setProductsSorted(availableProducts)
      } else {
        setProductsSorted(filteredProducts)
      }
    }
  }, [filteredProducts, updatedTariff, sortType])

  const sortProducts = (availableProducts: Product[]) => {
    if (sortType === SortType.Price) {
      return availableProducts.sort((a, b) =>
        getEstimatedPrice(a.services[0].id)! - getEstimatedPrice(b.services[0].id)! !== 0
          ? getEstimatedPrice(a.services[0].id)! - getEstimatedPrice(b.services[0].id)!
          : getDuration(a.services[0].id)! - getDuration(b.services[0].id)!
      )
    } else if (sortType === SortType.Time) {
      return availableProducts.sort((a, b) =>
        getDuration(a.services[0].id)! - getDuration(b.services[0].id)! !== 0
          ? getDuration(a.services[0].id)! - getDuration(b.services[0].id)!
          : getEstimatedPrice(a.services[0].id)! - getEstimatedPrice(b.services[0].id)!
      )
    } else if (sortType === SortType.Battery) {
      return availableProducts.sort((a, b) =>
        getTargetLevelLabel(b.services[0].id)! - getTargetLevelLabel(a.services[0].id)! !== 0
          ? getTargetLevelLabel(b.services[0].id)! - getTargetLevelLabel(a.services[0].id)!
          : getEstimatedPrice(a.services[0].id)! - getEstimatedPrice(b.services[0].id)!
      )
    }

    return availableProducts
  }

  useEffect(() => {
    if (productsSorted?.length === 0 || productsNumber === 0) {
      setNextDisabled(true)
      return
    }

    if (productsSorted && productsSorted.length > 0 && typeof updatedTariff !== 'undefined') {
      onAssetSelect('0', productsSorted[0])
    }
  }, [productsSorted, updatedTariff, currentStep, simulationTime, targetBatteryLevel])

  const getUpdatedTariff = () => {
    if (productsDetails.data) {
      let queryParams = null
      setIsLoading(true)
      setNextDisabled(true)

      if (simulationType === SimulationType.Battery) {
        queryParams = new URLSearchParams({
          batteryInitialLevel: initialBatteryLevel!.toString(),
          batteryTargetLevel: batteryLevel.toString(),
          productInfos: JSON.stringify(productsDetails.data.productMapping)
        })
        setSortType(SortType.Time)
      } else if (simulationType === SimulationType.Time) {
        queryParams = new URLSearchParams({
          batteryInitialLevel: initialBatteryLevel!.toString(),
          durationInMinutes: estimatedTime,
          productInfos: JSON.stringify(productsDetails.data.productMapping)
        })

        setSortType(SortType.Price)
      }

      if (typeof powerMaxStandard !== 'undefined') {
        queryParams?.append('powerMaxStandard', powerMaxStandard.toString())
      }

      setFilteredProducts(productsDetails.data.products)
      setChargingSessionParams(queryParams?.toString())
      setProductsNumber(productsDetails.data.nbCompatibleProducts!)
    }
  }

  const onBatteryLevelTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(event.target.value)

    if (!isNaN(newValue)) {
      newValue <= initialBatteryLevel! + 1 ? updateBatteryLevel(newValue + 1) : updateBatteryLevel(newValue)
    }
  }

  const onBatteryLevelSliderChangeCommited = async (
    _event: Event | React.SyntheticEvent<Element, Event>,
    _value: number | number[]
  ) => {
    getUpdatedTariff()
  }

  const onBatteryLevelSliderChange = async (_event: Event, value: number | number[]) => {
    const newValue = Number(value)

    if (!isNaN(newValue)) {
      newValue <= initialBatteryLevel! + 1 ? updateBatteryLevel(newValue + 1) : updateBatteryLevel(newValue)
    }
  }

  const updateBatteryLevel = (value: number | string) => {
    const userBatteryLevel = initialBatteryLevel!

    if ((value as number) < userBatteryLevel) {
      setBatteryLevel(userBatteryLevel)
      setTargetBatteryLevel(userBatteryLevel)
    } else if ((value as number) > 80) {
      setBatteryLevel(80)
      setTargetBatteryLevel(80)
    } else {
      setBatteryLevel(value)
      setTargetBatteryLevel(parseInt(value.toString()))
    }
  }

  const updateEstimatedTime = async () => {
    if (estimatedTime !== null) {
      setSimulationTime(estimatedTime)
      getUpdatedTariff()
    }
  }

  const onEstimatedTimeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value)

    if (value > parseInt(e.target.max)) {
      setEstimatedTime(e.target.max)
    } else if (value < parseInt(e.target.min) || !value) {
      setEstimatedTime(e.target.min)
    } else {
      setEstimatedTime(value.toString())
    }
  }

  const onAssetSelect = (index: string, selectedProduct: Product) => {
    setSelectedId(index)
    setSelectedProduct(selectedProduct)
    setNextDisabled(false)
  }

  const minToHours = (nbMinutes: number) => {
    const padTo2Digits = (num: number) => num.toString().padStart(2, '0')
    const minutes = nbMinutes % 60
    const hours = Math.floor(nbMinutes / 60)

    return `${padTo2Digits(hours)}:${padTo2Digits(minutes)}`
  }

  const getDuration = (serviceId: string) => {
    const durationInMinutes = updatedTariff?.find(
      currentTariff => currentTariff.serviceId === serviceId
    )?.durationInMinutes

    if (durationInMinutes) {
      return durationInMinutes
    }
  }

  const getTargetLevelLabel = (serviceId: string) => {
    const batteryTargetLevel = updatedTariff?.find(
      currentTariff => currentTariff.serviceId === serviceId
    )?.batteryTargetLevel

    if (batteryTargetLevel) {
      return Math.round(batteryTargetLevel)
    }
  }

  const getEstimatedPrice = (serviceId: string) => {
    const estimatedPrice = updatedTariff?.find(currentTariff => currentTariff.serviceId === serviceId)?.estimatedPrice

    if (!estimatedPrice) {
      return
    }

    return estimatedPrice
  }

  const compareInitialToTargetBatteryLevel = (serviceId: string) => {
    const selectedTariff = updatedTariff?.find(currentTariff => currentTariff.serviceId === serviceId)
    const durationInMinutes = selectedTariff?.durationInMinutes!
    const batteryTargetLevel = selectedTariff?.batteryTargetLevel!

    if (simulationType === SimulationType.Time && simulationTime && batteryTargetLevel === 80) {
      return durationInMinutes < parseInt(simulationTime)
    }
  }

  const handleSortChange = (sortType: SortType) => {
    setSortType(sortType)
  }

  const renderSimulationEnergyInputs = () => {
    return (
      <>
        <Typography variant="body1">{t('station-assets.body1')}</Typography>
        <div className="estimated-time-form">
          <TextField
            type="number"
            variant="outlined"
            size="small"
            onChange={onEstimatedTimeChange}
            value={estimatedTime}
            disabled={isLoading}
            className="estimated-time-input"
            inputProps={{
              min: 0,
              max: 45
            }}
            InputProps={{
              endAdornment: <Typography variant="subtitle2">min</Typography>
            }}
            required
          />
          <Button
            color="secondary"
            variant="text"
            component="div"
            onClick={updateEstimatedTime}
            disabled={estimatedTime === '0' || isLoading}
          >
            <span>OK</span>
          </Button>
        </div>
        {parseInt(estimatedTime) >= 45 && (
          <InformativeComponent color="#285AFF" icon="alert" content={t('station-assets.duration-alert')} />
        )}
        <span className="asset-disclaimer">{t('station-assets.disclaimer')}</span>
      </>
    )
  }

  const renderSimulationTimeInputs = () => {
    return (
      <>
        <Typography variant="body1">{t('station-assets.simulation-battery-level-label')}</Typography>
        <div className="slider-container">
          <Slider
            value={typeof batteryLevel === 'number' ? batteryLevel : 0}
            onChange={onBatteryLevelSliderChange}
            onChangeCommitted={onBatteryLevelSliderChangeCommited}
            aria-labelledby="battery-level-slider"
            min={initialBatteryLevel}
            max={80}
            marks={sliderMarks}
          />
          <TextField
            className="custom-input-small"
            value={batteryLevel}
            variant="outlined"
            size="small"
            onChange={onBatteryLevelTextChange}
            disabled={isLoading}
            aria-label="Battery level"
            inputProps={{
              step: 1,
              min: initialBatteryLevel! + 1,
              max: 80,
              type: 'number',
              'aria-labelledby': 'input-slider'
            }}
            InputProps={{
              readOnly: true,
              endAdornment: <Typography variant="subtitle2">%</Typography>
            }}
            required
          />
        </div>
        <InformativeComponent icon="alert" color="#285AFF" content={t('simulation-menu.slider-info')} />
        <span className="asset-disclaimer">{t('station-assets.disclaimer')}</span>
      </>
    )
  }

  const renderGlobalStatus = (product: Product) => {
    let statusClassName = ''
    let statusMessage = ''

    if (product.availableAssetCount > 0) {
      statusClassName = 'status-Available'
      statusMessage = 'status.Available'
    } else if (product.occupiedAssetCount > 0) {
      statusClassName = 'status-InUse'
      statusMessage = 'status.InUse'
    } else if (product.unknownAssetCount > 0) {
      statusClassName = 'status-Unknown'
      statusMessage = 'status.Unknown'
    }

    return (
      <Typography
        marginTop={1}
        marginLeft={1.5}
        className={`status ${statusClassName}`}
        variant="caption"
        component="span"
        color="primary"
      >
        {statusMessage != '' ? t(statusMessage) : ''}
      </Typography>
    )
  }

  const noProduct = () => {
    let message = null

    if (productsNumber === 0) {
      message = 'station-assets.no-product-compatible'
    } else if (productsSorted?.length === 0) {
      message = 'station-assets.no-product-available'
    }

    return message != null ? <Typography variant="body1">{t(message)}</Typography> : null
  }

  const isConnectorCompatible = (connector: string) => {
    if (
      basicConnectors?.standardPlug === connector ||
      (basicConnectors?.fastPlugObject?.fastPlug === connector &&
        basicConnectors?.fastPlugObject?.fastPlugOptional === false) ||
      optionalConnectors.indexOf(connector) !== -1
    ) {
      return true
    }

    return false
  }

  const isSelectedId = (id: string) => {
    if (selectedId === id) {
      return true
    }

    return false
  }

  const assetDisclaimer = (id: string) => {
    if (simulationType === SimulationType.Time && compareInitialToTargetBatteryLevel(id)) {
      return (
        <div className="list-row disclaimer-container">
          <div className="list-item-icon-disclaimer">
            <IconProvider id="alert" size="small" color="light-grey-filter" />
          </div>
          <div className="battery-level-disclaimer">{t('station-assets.battery-level-disclaimer')}</div>
        </div>
      )
    } else if (simulationType === SimulationType.Battery && getDuration(id)! > 45) {
      return (
        <div className="list-row disclaimer-container">
          <div className="list-item-icon-disclaimer">
            <IconProvider id="alert" size="small" color="light-grey-filter" />
          </div>
          <div className="time-disclaimer">{t('station-assets.time-disclaimer')}</div>
        </div>
      )
    }
  }

  return (
    <Stack textAlign="center">
      {!isLoading && noProduct() !== null ? (
        noProduct()
      ) : (
        <>
          {simulationType === SimulationType.Battery && renderSimulationTimeInputs()}
          {simulationType === SimulationType.Time && renderSimulationEnergyInputs()}
          {!isLoading && updatedTariff && (
            <>
              <div className="sort-select">
                <Select
                  className="test"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={sortType}
                  onChange={e => handleSortChange(e.target.value as SortType)}
                >
                  <MenuItem value={SortType.Price}>
                    <div className="sort-menu">{t('action.sort-price')}</div>
                  </MenuItem>
                  {simulationType === SimulationType.Battery && (
                    <MenuItem value={SortType.Time}>
                      <div className="sort-menu">{t('action.sort-time')}</div>
                    </MenuItem>
                  )}
                  {simulationType === SimulationType.Time && (
                    <MenuItem value={SortType.Battery}>
                      <div className="sort-menu">{t('action.sort-battery')}</div>
                    </MenuItem>
                  )}
                </Select>
              </div>
              <List>
                {productsSorted?.map((item: Product, key: any) => (
                  <div key={key}>
                    {isConnectorCompatible(item.services[0].connectors[0].standard) && (
                      <ListItem disablePadding disableGutters dense>
                        <ListItemButton
                          selected={isSelectedId(key.toString())}
                          onClick={() => onAssetSelect(key.toString(), item)}
                          className="list-item-button"
                        >
                          <div className="list-row">{renderGlobalStatus(item)}</div>
                          <div className="list-row">
                            <div className="list-item-icon">
                              <IconProvider
                                id={item.services[0].connectors[0].standard}
                                color={isSelectedId(key.toString()) ? 'red-filter' : ''}
                                size="large"
                              />
                              <span className="connector-standard">
                                {t('connector.' + item.services[0].connectors[0].standard)}
                              </span>
                            </div>
                            <div className="list-item-infos">
                              <div className="list-item-title">
                                <Typography color={isSelectedId(key.toString()) ? 'secondary' : 'primary'}>
                                  {t('power-type.' + item.services[0].connectors[0].powerType)}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={isSelectedId(key.toString()) ? 'secondary' : 'primary'}
                                >
                                  {t('station-assets.title', {
                                    power: item.services[0].connectors[0].displayPower,
                                    powerType: t('power-type.' + item.services[0].connectors[0].powerType)
                                  })}
                                </Typography>
                              </div>
                              <div className="station-assets-chip">
                                <div className="list-item-details">
                                  <IconProvider id="time" color="red-filter" size="small" />
                                  <Chip
                                    label={
                                      (getDuration(item.services[0].id) &&
                                        minToHours(getDuration(item.services[0].id)!)) ??
                                      '...'
                                    }
                                    size="small"
                                  />
                                </div>
                                <div className="list-item-details">
                                  <IconProvider id="price" color="red-filter" size="small" />
                                  <Chip
                                    label={
                                      getEstimatedPrice(item.services[0].id) !== undefined
                                        ? Math.round(getEstimatedPrice(item.services[0].id)! * 100 * 1.2) / 100 + '€'
                                        : '...'
                                    }
                                    size="small"
                                  />
                                </div>
                                <div className="list-item-details">
                                  <IconProvider id="battery" color="red-filter" size="small" />
                                  <Chip label={getTargetLevelLabel(item.services[0].id) + '%' ?? '...'} size="small" />
                                </div>
                              </div>
                            </div>
                          </div>
                          {assetDisclaimer(item.services[0].id)}
                        </ListItemButton>
                      </ListItem>
                    )}
                  </div>
                ))}
              </List>
            </>
          )}
        </>
      )}
    </Stack>
  )
}

export default StationAssets
